import React from 'react'
import { FormattedMessage } from 'react-intl'

const resolveConfigValue = (value, context) => (typeof value === 'function' ? value(context) : value)

const APPLICATION_REGISTRATION_POLICY_WARNING_MESSAGE_ID = 'SECURE.APPLICATION_REGISTRATION_POLICY_WARNING'
const APPLICATION_REGISTRATION_UNKNOWN_OPTION = {
  label: 'Unknown',
  value: null,
  isDisabled: true,
}
const APPLICATION_REGISTRATION_STATUS_OPTIONS = [
  {
    label: 'Disabled',
    value: false,
    bestPractice: true,
  },
  {
    label: 'Enabled',
    value: true,
  },
]

const getApplicationRegistrationStatusOptions = ({ currentSetting }) =>
  currentSetting?.status === 'Unknown'
    ? [APPLICATION_REGISTRATION_UNKNOWN_OPTION, ...APPLICATION_REGISTRATION_STATUS_OPTIONS]
    : APPLICATION_REGISTRATION_STATUS_OPTIONS

const TENANT_CREATION_PENDING_OPTION = {
  label: 'First run pending',
  value: null,
  isDisabled: true,
}
const TENANT_CREATION_STATUS_OPTIONS = [
  {
    label: 'Enabled',
    value: false,
    bestPractice: true,
  },
  {
    label: 'Disabled',
    value: true,
  },
]

const getTenantCreationStatusOptions = ({ currentSetting }) =>
  currentSetting?.status === TENANT_CREATION_PENDING_OPTION.label
    ? [TENANT_CREATION_PENDING_OPTION, ...TENANT_CREATION_STATUS_OPTIONS]
    : TENANT_CREATION_STATUS_OPTIONS

const SECURITY_GROUP_CREATION_PENDING_OPTION = {
  label: 'First run pending',
  value: null,
  isDisabled: true,
}
const SECURITY_GROUP_CREATION_STATUS_OPTIONS = [
  {
    label: 'No',
    value: false,
    bestPractice: true,
  },
  {
    label: 'Yes',
    value: true,
  },
]

const getSecurityGroupCreationStatusOptions = ({ currentSetting }) =>
  currentSetting?.status === SECURITY_GROUP_CREATION_PENDING_OPTION.label
    ? [SECURITY_GROUP_CREATION_PENDING_OPTION, ...SECURITY_GROUP_CREATION_STATUS_OPTIONS]
    : SECURITY_GROUP_CREATION_STATUS_OPTIONS

const CALENDAR_SHARING_NOT_MEASURED_STATUS = 'Not Measured'
const CALENDAR_SHARING_SETTING_TITLE = 'Default Sharing Policy'
const CALENDAR_SHARING_UNSET_STATUS = 'Unset'
const CALENDAR_SHARING_WARNING_MESSAGE_IDS = Object.freeze({
  compliant: 'SECURE.CALENDAR_SHARING.MISSING_EXCHANGE_ONLINE_COMPLIANT_WARNING',
  generic: 'SECURE.CALENDAR_SHARING.MISSING_EXCHANGE_ONLINE_WARNING',
  nonCompliant: 'SECURE.CALENDAR_SHARING.MISSING_EXCHANGE_ONLINE_NON_COMPLIANT_WARNING',
})
const CALENDAR_SHARING_STATUS_OPTIONS = [
  {
    label: 'Disabled',
    value: false,
    bestPractice: true,
  },
  {
    label: 'Enabled',
    value: true,
  },
]
const CALENDAR_SHARING_UNSET_OPTION = {
  label: 'Not set',
  value: CALENDAR_SHARING_UNSET_STATUS,
  isDisabled: true,
}
const DISABLE_SMTP_AUTH_NOT_MEASURED_STATUS = 'Not Measured'
const DISABLE_SMTP_AUTH_UNKNOWN_STATUS = 'Not measured'
const DISABLE_SMTP_AUTH_WARNING_MESSAGE_ID = 'SECURE.DISABLE_SMTP_AUTH.MISSING_EXCHANGE_ONLINE_WARNING'
const DISABLE_SMTP_AUTH_STATUS_OPTIONS = [
  {
    label: 'Disabled',
    value: true,
    bestPractice: true,
  },
  {
    label: 'Enabled',
    value: false,
  },
]
const DISABLE_SMTP_AUTH_UNKNOWN_OPTION = {
  label: DISABLE_SMTP_AUTH_UNKNOWN_STATUS,
  value: null,
  isDisabled: true,
}

const getCalendarSharingSetting = (securityCheck) =>
  securityCheck?.compliance?.settings?.find((setting) => setting.title === CALENDAR_SHARING_SETTING_TITLE) ?? null

const isMissingExchangeOnlineCalendarSharingState = (securityCheck) =>
  securityCheck?.details?.defaultCalendarSharingPolicyInferred === true &&
  securityCheck?.configuration?.status === CALENDAR_SHARING_NOT_MEASURED_STATUS

const getCalendarSharingMissingExchangeOnlineWarningMessageId = (securityCheck) => {
  if (!isMissingExchangeOnlineCalendarSharingState(securityCheck)) {
    return null
  }

  const compliant = getCalendarSharingSetting(securityCheck)?.compliant

  if (compliant === true) {
    return CALENDAR_SHARING_WARNING_MESSAGE_IDS.compliant
  }

  if (compliant === false) {
    return CALENDAR_SHARING_WARNING_MESSAGE_IDS.nonCompliant
  }

  return CALENDAR_SHARING_WARNING_MESSAGE_IDS.generic
}

const getCalendarSharingStatusOptions = ({ currentSetting, isDisabled }) => {
  if (isDisabled && !currentSetting) {
    return [{ ...CALENDAR_SHARING_UNSET_OPTION, bestPractice: true }, ...CALENDAR_SHARING_STATUS_OPTIONS]
  }

  if (isDisabled || currentSetting?.status === CALENDAR_SHARING_UNSET_STATUS) {
    return [CALENDAR_SHARING_UNSET_OPTION, ...CALENDAR_SHARING_STATUS_OPTIONS]
  }

  return CALENDAR_SHARING_STATUS_OPTIONS
}

const getCalendarSharingCheckModalAlertContent = ({ securityCheck }) => {
  const messageId = getCalendarSharingMissingExchangeOnlineWarningMessageId(securityCheck)

  if (!messageId) {
    return null
  }

  return <FormattedMessage id={messageId} />
}

const isMissingExchangeOnlineDisableSmtpAuthState = (securityCheck) =>
  securityCheck?.details?.smtpClientAuthenticationDisabledInferred === true &&
  securityCheck?.configuration?.status === DISABLE_SMTP_AUTH_NOT_MEASURED_STATUS

const getDisableSmtpAuthMissingExchangeOnlineWarningMessageId = (securityCheck) =>
  isMissingExchangeOnlineDisableSmtpAuthState(securityCheck) ? DISABLE_SMTP_AUTH_WARNING_MESSAGE_ID : null

const getDisableSmtpAuthStatusOptions = ({ currentSetting, isDisabled }) => {
  if (isDisabled && !currentSetting) {
    return [DISABLE_SMTP_AUTH_UNKNOWN_OPTION, ...DISABLE_SMTP_AUTH_STATUS_OPTIONS]
  }

  if (isDisabled || currentSetting?.status === DISABLE_SMTP_AUTH_UNKNOWN_STATUS) {
    return [DISABLE_SMTP_AUTH_UNKNOWN_OPTION, ...DISABLE_SMTP_AUTH_STATUS_OPTIONS]
  }

  return DISABLE_SMTP_AUTH_STATUS_OPTIONS
}

const getDisableSmtpAuthCheckModalAlertContent = ({ securityCheck }) => {
  const messageId = getDisableSmtpAuthMissingExchangeOnlineWarningMessageId(securityCheck)

  if (!messageId) {
    return null
  }

  return <FormattedMessage id={messageId} />
}

const ACTIVITY_BASED_TIMEOUT_ACTIVATION_SETTING_TITLE = 'Activate organization default settings'
const ACTIVITY_BASED_TIMEOUT_ACTIVATION_DISABLED_STATUS = 'Disabled'
const ACTIVITY_BASED_TIMEOUT_ACTIVATION_DISABLED_TOOLTIP_ID =
  'SECURE.ACTIVITY_BASED_TIMEOUT.ACTIVATION_DISABLED_TOOLTIP'
const ACTIVITY_BASED_TIMEOUT_STATUS_OPTIONS = [
  {
    label: '01 Hour',
    value: '01:00:00',
  },
  {
    label: '03 Hours',
    value: '03:00:00',
  },
  {
    label: '06 Hours',
    value: '06:00:00',
  },
  {
    label: '08 Hours',
    value: '08:00:00',
  },
  {
    label: '12 Hours',
    value: '12:00:00',
  },
  {
    label: '24 Hours',
    value: '1.0:00:00',
  },
]
const ACTIVITY_BASED_TIMEOUT_NOT_SET_OPTION = {
  label: <FormattedMessage id="SECURE.ACTIVITY_BASED_TIMEOUT.NOT_SET" />,
  value: null,
  isDisabled: true,
}

const isActivityBasedTimeoutActivationDisabled = (securityCheck) =>
  securityCheck?.compliance?.settings?.find(({ title }) => title === ACTIVITY_BASED_TIMEOUT_ACTIVATION_SETTING_TITLE)
    ?.status === ACTIVITY_BASED_TIMEOUT_ACTIVATION_DISABLED_STATUS

const getActivityBasedTimeoutStatusOptions = ({ isDisabled }) =>
  isDisabled
    ? [ACTIVITY_BASED_TIMEOUT_NOT_SET_OPTION, ...ACTIVITY_BASED_TIMEOUT_STATUS_OPTIONS]
    : ACTIVITY_BASED_TIMEOUT_STATUS_OPTIONS

export {
  ACTIVITY_BASED_TIMEOUT_ACTIVATION_DISABLED_TOOLTIP_ID,
  ACTIVITY_BASED_TIMEOUT_ACTIVATION_SETTING_TITLE,
  APPLICATION_REGISTRATION_POLICY_WARNING_MESSAGE_ID,
  getActivityBasedTimeoutStatusOptions,
  getDisableSmtpAuthCheckModalAlertContent,
  getDisableSmtpAuthMissingExchangeOnlineWarningMessageId,
  getDisableSmtpAuthStatusOptions,
  getApplicationRegistrationStatusOptions,
  getSecurityGroupCreationStatusOptions,
  getTenantCreationStatusOptions,
  getCalendarSharingCheckModalAlertContent,
  getCalendarSharingMissingExchangeOnlineWarningMessageId,
  getCalendarSharingStatusOptions,
  isActivityBasedTimeoutActivationDisabled,
  isMissingExchangeOnlineDisableSmtpAuthState,
  isMissingExchangeOnlineCalendarSharingState,
  resolveConfigValue,
}
